import "@/App.css";
import { useEffect, useState } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { isCustomHost } from "@/lib/api";
import Login from "@/pages/Login";
import Join from "@/pages/Join";
import ReferralLanding from "@/pages/ReferralLanding";
import Confirm from "@/pages/Confirm";
import Rebook from "@/pages/Rebook";
import BrandPreview from "@/pages/BrandPreview";
import Landing from "@/pages/Landing";
import BusinessEntry from "@/pages/BusinessEntry";
import Signup from "@/pages/Signup";
import PaymentResult from "@/pages/client/Payments";
import Payments from "@/pages/admin/Payments";
import AdminLayout from "@/pages/admin/AdminLayout";
import Overview from "@/pages/admin/Overview";
import Schedule from "@/pages/admin/Schedule";
import Clients from "@/pages/admin/Clients";
import Reports from "@/pages/admin/Reports";
import Messages from "@/pages/admin/Messages";
import Automations from "@/pages/admin/Automations";
import PlatformTour from "@/pages/admin/PlatformTour";
import Settings from "@/pages/admin/Settings";
import Library from "@/pages/admin/Library";
import Programs from "@/pages/admin/Programs";
import ClientPortal from "@/pages/client/ClientPortal";

const Loading = () => <div className="center" data-testid="app-loading"><span className="spin" /> Loading</div>;

function Guard({ role, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "ADMIN" ? "/admin" : "/client"} replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "ADMIN" ? "/admin" : "/client"} replace />;
}

// On a trainer's custom domain, resolve the business once and route "/" to its branded portal entry.
function CustomHostGate({ children }) {
  const [slug, setSlug] = useState(() => (isCustomHost() ? sessionStorage.getItem("ip_biz") || null : false));
  useEffect(() => {
    if (slug !== null) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/business/by-host`, { params: { host: window.location.host } })
      .then((r) => { sessionStorage.setItem("ip_biz", r.data.slug); setSlug(r.data.slug); })
      .catch(() => setSlug(false));
  }, [slug]);
  if (slug === null) return <Loading />;
  if (slug && window.location.pathname === "/") return <Navigate to={`/b/${slug}`} replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <CustomHostGate>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/join/:token" element={<Join />} />
          <Route path="/b/:slug" element={<BusinessEntry />} />
          <Route path="/b/:slug/join" element={<ReferralLanding />} />
          <Route path="/app" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/confirm/:token" element={<Confirm />} />
          <Route path="/rebook/:token" element={<Rebook />} />
          <Route path="/preview/:token" element={<BrandPreview />} />
          <Route path="/payment/success" element={<PaymentResult ok />} />
          <Route path="/payment/cancel" element={<PaymentResult ok={false} />} />
          <Route path="/admin" element={<Guard role="ADMIN"><AdminLayout /></Guard>}>
            <Route index element={<Overview />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="clients" element={<Clients />} />
            <Route path="reports" element={<Reports />} />
            <Route path="messages" element={<Messages />} />
            <Route path="automations" element={<Automations />} />
            <Route path="tour" element={<PlatformTour />} />
            <Route path="settings" element={<Settings />} />
            <Route path="library" element={<Library />} />
            <Route path="programs" element={<Programs />} />
            <Route path="payments" element={<Payments />} />
          </Route>
          <Route path="/client" element={<Guard role="CLIENT"><ClientPortal /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </CustomHostGate>
      </BrowserRouter>
      <Toaster position="bottom-right" toastOptions={{ style: { fontFamily: "inherit", fontSize: 13 } }} />
    </AuthProvider>
  );
}
